import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Scissors,
  TrendingUp,
  DollarSign,
  Clock,
  Layers,
  Activity,
  ChevronDown,
} from "lucide-react";
import { GlassPanel, formatCrmCurrency, formatNumber, ThemedLegend, getAxisProps, getGridProps, getAngledAxisProps, getTooltipComponent, CATEGORY_COLORS, CATEGORY_GRADIENTS, IncompleteState } from "./ReportShared";
import { useCrmLocale } from "../../SalonCRM/i18n/CrmLocale";
import { DateRange, filterMonthly } from "../analyticsDateRange";
import type { LiveAnalytics, MonthlyServiceRow } from "../liveAnalyticsAdapter";

const CATEGORY_KEYS = ["Color", "Highlights", "Toner", "Straightening", "Treatment", "Others"] as const;
const MATERIAL_DAY_PAGE_SIZE = 20;

const ServicesReport: React.FC<{ dateRange: DateRange; isDark: boolean; analytics: LiveAnalytics }> = ({ dateRange, isDark, analytics }) => {
  const { lang, t } = useCrmLocale();
  const r = t.analytics.report;
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [visibleDayRows, setVisibleDayRows] = useState(MATERIAL_DAY_PAGE_SIZE);

  useEffect(() => {
    setVisibleDayRows(MATERIAL_DAY_PAGE_SIZE);
  }, [expandedCategory]);
  const categoryLabel = (category: string) => {
    const displayKey = category === "Cut" || category === "Other" ? "Others" : category;
    return t.analytics.categories[displayKey as keyof typeof t.analytics.categories] ?? category;
  };
  const fc = (v: number) => formatCrmCurrency(v, lang);
  const formatDay = (day: string) => {
    const d = new Date(`${day}T12:00:00`);
    if (!Number.isFinite(d.getTime())) return day;
    return d.toLocaleDateString(lang === "he" ? "he-IL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const SERVICES = analytics.services;
  const MONTHLY_SERVICES = analytics.monthlyServices;
  const materialDaysByCategory = useMemo(() => {
    const map = new Map<string, typeof analytics.categoryMaterialDays>();
    for (const row of analytics.categoryMaterialDays || []) {
      const list = map.get(row.category) ?? [];
      list.push(row);
      map.set(row.category, list);
    }
    return map;
  }, [analytics.categoryMaterialDays]);

  const f = useMemo(() => {
    const months = filterMonthly(MONTHLY_SERVICES, dateRange);

    const totalPerformed = months.reduce((s, m) => s + m.total, 0);
    const totalRevenue = months.reduce((s, m) => s + m.revenue, 0);
    const avgPrice = totalPerformed > 0 ? Math.round(totalRevenue / totalPerformed) : 0;

    const rawCats = CATEGORY_KEYS.map(cat => {
      const performed = months.reduce((s, m) => s + ((m[cat as keyof MonthlyServiceRow] as number) || 0), 0);
      const svcs = cat === "Others"
        ? SERVICES.filter(sv => sv.category === "Cut" || sv.category === "Other")
        : SERVICES.filter(sv => sv.category === cat);
      // Prefer customer×category×day recorded average (bowls summed once per client-day).
      const recordedCatAvg = analytics.categoryAvgMaterialCost?.[cat];
      const avgMatCost = typeof recordedCatAvg === "number"
        ? recordedCatAvg
        : (svcs.length > 0 ? Math.round(svcs.reduce((sum, sv) => sum + sv.avgMaterialCost, 0) / svcs.length) : 0);
      const weightedAvgPrice = svcs.reduce((sum, sv) => sum + sv.avgPrice * sv.totalPerformed, 0) / Math.max(1, svcs.reduce((sum, sv) => sum + sv.totalPerformed, 0));
      return { name: cat, totalPerformed: performed, rawRevenue: performed * weightedAvgPrice, avgMaterialCost: avgMatCost, serviceCount: svcs.length };
    }).filter(c => c.totalPerformed > 0);
    const rawRevenueTotal = rawCats.reduce((sum, c) => sum + c.rawRevenue, 0);
    const normalizedCats = rawCats.map((cat) => ({
      ...cat,
      totalRevenue: rawRevenueTotal > 0 ? Math.round((cat.rawRevenue / rawRevenueTotal) * totalRevenue) : 0,
    }));
    const revenueDelta = totalRevenue - normalizedCats.reduce((sum, cat) => sum + cat.totalRevenue, 0);
    if (normalizedCats.length > 0 && revenueDelta !== 0) {
      const largestIndex = normalizedCats.reduce(
        (bestIndex, cat, index, all) => cat.totalRevenue > all[bestIndex].totalRevenue ? index : bestIndex,
        0,
      );
      normalizedCats[largestIndex] = {
        ...normalizedCats[largestIndex],
        totalRevenue: normalizedCats[largestIndex].totalRevenue + revenueDelta,
      };
    }
    const filteredCats = normalizedCats.sort((a, b) => b.totalPerformed - a.totalPerformed);

    const avgMatCostPerSvc = totalPerformed > 0
      ? Math.round(SERVICES.reduce((s, sv) => s + sv.avgMaterialCost * sv.totalPerformed, 0) / SERVICES.reduce((s, sv) => s + sv.totalPerformed, 0))
      : 0;
    const profitMarginAvg = avgPrice > 0 ? Math.round(((avgPrice - avgMatCostPerSvc) / avgPrice) * 100) : 0;
    const topCat = filteredCats[0];
    const topCatPct = topCat && totalPerformed > 0 ? Math.round((topCat.totalPerformed / totalPerformed) * 100) : 0;

    return { months, totalPerformed, totalRevenue, avgPrice, avgMatCostPerSvc, profitMarginAvg, filteredCats, topCat, topCatPct };
  }, [dateRange, SERVICES, MONTHLY_SERVICES, analytics.categoryAvgMaterialCost]);

  const rankedServices = [...SERVICES].sort((a, b) => b.totalPerformed - a.totalPerformed);

  const pieByCat = f.filteredCats.map(c => ({ name: categoryLabel(c.name), key: c.name, value: c.totalPerformed }));
  const revenueByCat = f.filteredCats.map(c => ({ name: categoryLabel(c.name), key: c.name, revenue: c.totalRevenue, color: CATEGORY_COLORS[c.name] || "#64748B" }));

  const txt = isDark ? "text-white" : "text-[#1A1A1A]";
  const txtMuted = isDark ? "text-gray-500" : "text-gray-500";
  const txtMid = isDark ? "text-gray-400" : "text-gray-500";
  const txtFaint = isDark ? "text-gray-600" : "text-gray-400";
  const borderSep = isDark ? "border-white/[0.06]" : "border-black/[0.06]";
  const cardBg = isDark ? "bg-white/[0.04]" : "bg-black/[0.03]";
  const cardBorder = isDark ? "border-white/[0.04]" : "border-black/[0.04]";
  const hoverBg = isDark ? "hover:bg-white/[0.08]" : "hover:bg-black/[0.04]";
  const barBg = isDark ? "bg-white/[0.06]" : "bg-black/[0.06]";
  const stripeBg = isDark ? "bg-white/[0.02]" : "bg-black/[0.02]";
  const stripeHover = isDark ? "hover:bg-white/[0.06]" : "hover:bg-black/[0.04]";
  const stripeBorder = isDark ? "border-white/[0.04]" : "border-black/[0.04]";

  const TooltipComp = getTooltipComponent(isDark);
  const axisProps = getAxisProps(isDark);
  const gridProps = getGridProps(isDark);
  const angledAxisProps = getAngledAxisProps(isDark);

  const pieTooltipStyle = isDark
    ? { background: "rgba(10,10,14,0.88)", backdropFilter: "blur(16px)", boxShadow: "0 8px 32px rgba(0,0,0,0.50)" }
    : { background: "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)", boxShadow: "0 8px 32px rgba(0,0,0,0.10)" };
  const pieTooltipBorder = isDark ? "border-white/[0.08]" : "border-black/[0.06]";
  const pieTooltipName = isDark ? "text-white" : "text-gray-900";
  const pieTooltipVal = isDark ? "text-gray-400" : "text-gray-500";

  if (SERVICES.length === 0) {
    return (
      <IncompleteState
        isDark={isDark}
        title={r.noServicesTitle}
        description={r.noServicesDescription}
      />
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ── KPI Cards ───────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {([
          { icon: Scissors,   label: r.totalServices, value: formatNumber(f.totalPerformed, lang), gradient: "from-pink-500 to-rose-600", subtitle: `${SERVICES.length} ${r.serviceTypes}` },
          { icon: DollarSign, label: r.bookedRevenueEstimated, value: fc(f.totalRevenue), gradient: "from-emerald-500 to-teal-600", subtitle: `~${fc(f.avgPrice)} ${r.averagePrice}` },
          { icon: Activity,   label: r.avgMaterialCost, value: fc(f.avgMatCostPerSvc), gradient: "from-amber-500 to-orange-600", subtitle: `${f.profitMarginAvg}% ${r.estimatedMargin}` },
          { icon: Clock,      label: r.topCategory, value: f.topCat ? categoryLabel(f.topCat.name) : "–", gradient: "from-violet-500 to-purple-600", subtitle: `${f.topCatPct}% ${r.ofAllServices}` },
        ] as const).map(({ icon: Icon, label, value, gradient, subtitle }) => (
          <GlassPanel key={label} variant="chartDark" isDark={isDark} className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`} style={{ boxShadow: "0 0 16px rgba(0,0,0,0.3)" }}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className={`text-[11px] ${txtMuted} font-medium`}>{label}</p>
                <p className={`text-lg sm:text-xl font-bold ${txt} tracking-tight`}>{value}</p>
                <p className={`text-[10px] ${txtMuted} mt-0.5`}>{subtitle}</p>
              </div>
            </div>
          </GlassPanel>
        ))}
      </div>

      {/* ── Category Intelligence Cards ─────────────────── */}
      <GlassPanel variant="chartDark" isDark={isDark} className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
            <Layers className="w-3.5 h-3.5 text-white" />
          </div>
          <h3 className={`text-[14px] font-bold ${txt}`}>{r.serviceCategories}</h3>
          <span className={`text-[11px] ${txtMuted} ms-1`}>{r.performanceOverview}</span>
        </div>

        <div className="space-y-3">
          {f.filteredCats.map((cat) => {
            const pct = f.totalPerformed > 0 ? Math.round((cat.totalPerformed / f.totalPerformed) * 100) : 0;
            const color = CATEGORY_COLORS[cat.name] || "#64748B";
            const grad = CATEGORY_GRADIENTS[cat.name];
            const isOpen = expandedCategory === cat.name;
            const dayRows = materialDaysByCategory.get(cat.name) ?? [];
            const visibleRows = isOpen ? dayRows.slice(0, visibleDayRows) : [];
            const hasMoreRows = isOpen && visibleDayRows < dayRows.length;
            return (
              <div
                key={cat.name}
                className={`rounded-2xl ${cardBg} border ${cardBorder} transition-all duration-300 overflow-hidden`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedCategory(isOpen ? null : cat.name)}
                  className={`flex w-full items-center gap-4 p-4 text-start ${hoverBg} transition-all duration-300`}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: grad ? `linear-gradient(135deg, ${grad[0]}22, ${grad[1]}22)` : `${color}14`, border: `1px solid ${color}25` }}
                  >
                    <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}60` }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${txt}`}>{categoryLabel(cat.name)}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`text-[11px] ${txtMuted}`}>{formatNumber(cat.totalPerformed, lang)} {r.services}</span>
                      <span className={`text-[11px] ${txtFaint}`}>&middot;</span>
                      <span className={`text-[11px] ${txtMuted}`}>{cat.serviceCount} {r.types}</span>
                      <span className={`text-[11px] ${txtFaint}`}>&middot;</span>
                      <span className={`text-[11px] ${txtMuted}`}>~{fc(cat.avgMaterialCost)} {r.material}</span>
                      <span className={`text-[11px] ${txtFaint}`}>&middot;</span>
                      <span className={`text-[11px] ${txtMuted}`}>
                        {formatNumber(dayRows.length, lang)} {r.clientDaysLastYear}
                      </span>
                    </div>
                    <div className={`mt-2 w-full h-1.5 rounded-full ${barBg} overflow-hidden`}>
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: grad ? `linear-gradient(90deg, ${grad[0]}, ${grad[1]})` : color }}
                      />
                    </div>
                  </div>
                  <div className="text-end flex-shrink-0 ps-2">
                    <p className={`text-xl font-bold ${txt}`}>{fc(cat.totalRevenue)}</p>
                    <p className={`text-[10px] ${txtMuted} font-medium mt-0.5`}>{pct}% {r.ofTotal}</p>
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 flex-shrink-0 transition-transform ${txtMuted} ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {isOpen && (
                  <div className={`border-t px-3 pb-3 pt-3 sm:px-4 ${borderSep}`}>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <p className={`text-[11px] ${txtMuted}`}>{r.clientDayMaterialHint}</p>
                      {dayRows.length > 0 && (
                        <p className={`text-[11px] font-semibold ${txtMid}`}>
                          {formatNumber(dayRows.length, lang)} {r.rows}
                          {" · "}
                          {fc(dayRows.reduce((sum, row) => sum + row.totalCost, 0))}
                          {" · "}
                          {formatNumber(dayRows.reduce((sum, row) => sum + row.bowlCount, 0), lang)} {r.bowls}
                        </p>
                      )}
                    </div>
                    {dayRows.length === 0 ? (
                      <p className={`rounded-xl px-3 py-4 text-center text-[12px] ${txtFaint} ${stripeBg}`}>
                        {r.noClientDayMaterials}
                      </p>
                    ) : (
                      <>
                        <p className={`mb-2 text-[11px] ${txtMuted}`}>
                          {r.showingRowsOf
                            .replace("{shown}", formatNumber(visibleRows.length, lang))
                            .replace("{total}", formatNumber(dayRows.length, lang))}
                        </p>
                        <div
                          className={`max-h-96 overflow-auto rounded-xl border ${stripeBorder}`}
                          onScroll={(event) => {
                            if (!hasMoreRows) return;
                            const el = event.currentTarget;
                            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
                              setVisibleDayRows((n) => Math.min(n + MATERIAL_DAY_PAGE_SIZE, dayRows.length));
                            }
                          }}
                        >
                          <table className="min-w-full text-start text-[12px]">
                            <thead className={`sticky top-0 z-10 ${isDark ? "bg-[#16161f]" : "bg-[#f7f5f2]"} ${txtMuted}`}>
                              <tr className={`border-b ${borderSep}`}>
                                <th className="whitespace-nowrap px-3 py-2.5 font-semibold">{r.date}</th>
                                <th className="whitespace-nowrap px-3 py-2.5 font-semibold">{r.client}</th>
                                <th className="min-w-[140px] px-3 py-2.5 font-semibold">{r.service}</th>
                                <th className="min-w-[140px] px-3 py-2.5 font-semibold">{r.materials}</th>
                                <th className="whitespace-nowrap px-3 py-2.5 text-end font-semibold">{r.bowls}</th>
                                <th className="whitespace-nowrap px-3 py-2.5 text-end font-semibold">{r.visits}</th>
                                <th className="whitespace-nowrap px-3 py-2.5 text-end font-semibold">{r.grams}</th>
                                <th className="whitespace-nowrap px-3 py-2.5 text-end font-semibold">{r.totalCost}</th>
                                <th className="whitespace-nowrap px-3 py-2.5 text-end font-semibold">{r.avgPerBowl}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visibleRows.map((row, idx) => (
                                <tr
                                  key={row.id}
                                  className={`border-b last:border-b-0 ${borderSep} ${
                                    idx % 2 === 0 ? stripeBg : ""
                                  } ${stripeHover}`}
                                >
                                  <td className={`whitespace-nowrap px-3 py-2.5 ${txtMid}`}>{formatDay(row.day)}</td>
                                  <td className={`max-w-[160px] truncate px-3 py-2.5 font-semibold ${txt}`} title={row.customerName}>
                                    {row.customerName}
                                  </td>
                                  <td className={`max-w-[200px] px-3 py-2.5 ${txtMid}`} title={row.serviceNames.join(" · ")}>
                                    <span className="line-clamp-2">{row.serviceNames.join(" · ") || "—"}</span>
                                  </td>
                                  <td className={`max-w-[220px] px-3 py-2.5 ${txtFaint}`} title={row.materialLabels.join(" · ")}>
                                    <span className="line-clamp-2">{row.materialLabels.join(" · ") || "—"}</span>
                                  </td>
                                  <td className={`whitespace-nowrap px-3 py-2.5 text-end tabular-nums ${txt}`}>
                                    {formatNumber(row.bowlCount, lang)}
                                  </td>
                                  <td className={`whitespace-nowrap px-3 py-2.5 text-end tabular-nums ${txtMid}`}>
                                    {formatNumber(row.visitCount, lang)}
                                  </td>
                                  <td className={`whitespace-nowrap px-3 py-2.5 text-end tabular-nums ${txtMid}`}>
                                    {formatNumber(row.totalGrams, lang)}g
                                  </td>
                                  <td className={`whitespace-nowrap px-3 py-2.5 text-end tabular-nums font-semibold ${txt}`}>
                                    {fc(row.totalCost)}
                                  </td>
                                  <td className={`whitespace-nowrap px-3 py-2.5 text-end tabular-nums ${txtMid}`}>
                                    {fc(row.avgCostPerBowl)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {hasMoreRows && (
                            <p className={`sticky bottom-0 py-2 text-center text-[11px] ${txtMuted} ${isDark ? "bg-[#16161f]/99" : "bg-[#f7f5f2]/99"}`}>
                              {r.loadMoreRows}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </GlassPanel>

      {/* ── Service Mix Pie + Revenue by Category ───────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Service Mix Pie */}
        <GlassPanel variant="chartDark" isDark={isDark} className="p-0 overflow-hidden">
          <div className={`px-5 py-3.5 sm:px-6 sm:py-4 border-b ${borderSep} flex items-center gap-2.5`}>
            <Layers className="w-4 h-4 text-pink-400" style={{ filter: "drop-shadow(0 0 6px rgba(232,67,147,0.5))" }} />
            <h3 className={`text-[13px] font-bold ${txt}`}>{r.serviceMix}</h3>
            <span className={`text-[10px] ${txtMuted} ms-1`}>{f.topCat ? categoryLabel(f.topCat.name) : "–"} {r.leads} · {f.topCatPct}%</span>
          </div>
          <div className="p-4 sm:p-6">
            <div className="relative">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <defs>
                    {pieByCat.map((entry) => {
                      const c = CATEGORY_COLORS[entry.key] || "#64748B";
                      return (
                        <filter key={`glow-${entry.key}`} id={`pieGlow-${entry.key}`}>
                          <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={c} floodOpacity="0.4" />
                        </filter>
                      );
                    })}
                  </defs>
                  <Pie
                    data={pieByCat}
                    cx="50%"
                    cy="50%"
                    innerRadius={56}
                    outerRadius={108}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="none"
                    cornerRadius={6}
                  >
                    {pieByCat.map((entry) => (
                      <Cell key={entry.name} fill={CATEGORY_COLORS[entry.name] || "#64748B"} fillOpacity={0.9} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }: any) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0];
                      return (
                        <div className={`rounded-xl p-3 text-sm border ${pieTooltipBorder}`} style={pieTooltipStyle}>
                          <p className={`font-semibold ${pieTooltipName}`}>{d.name}</p>
                          <p className={pieTooltipVal}>{formatNumber(d.value, lang)} {r.services}</p>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className={`text-[9px] ${txtMuted} uppercase tracking-[0.15em]`}>{r.top}</p>
                <p className={`text-xl font-black ${txt} tracking-tight`}>{f.topCat ? categoryLabel(f.topCat.name) : "–"}</p>
                <p className={`text-[11px] ${txtMid} font-medium`}>{f.topCatPct}%</p>
              </div>
            </div>
            <div className="mt-3">
              <ThemedLegend isDark={isDark} items={pieByCat.map(e => ({ label: e.name, color: CATEGORY_COLORS[e.key] || "#64748B" }))} />
            </div>
          </div>
        </GlassPanel>

        {/* Revenue by Category */}
        <GlassPanel variant="chartDark" isDark={isDark} className="p-0 overflow-hidden">
          <div className={`px-5 py-3.5 sm:px-6 sm:py-4 border-b ${borderSep} flex items-center gap-2.5`}>
            <DollarSign className="w-4 h-4 text-emerald-400" style={{ filter: "drop-shadow(0 0 6px rgba(16,185,129,0.5))" }} />
            <h3 className={`text-[13px] font-bold ${txt}`}>{r.revenueByCategory}</h3>
          </div>
          <div className="p-4 sm:p-6">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={revenueByCat}>
                <defs>
                  {revenueByCat.map((entry) => {
                    const grad = CATEGORY_GRADIENTS[entry.key];
                    const c0 = grad ? grad[0] : entry.color;
                    const c1 = grad ? grad[1] : entry.color;
                    return (
                      <linearGradient key={`svcRevBar-${entry.key}`} id={`svcRevBar-${entry.key}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={c0} stopOpacity={0.90} />
                        <stop offset="100%" stopColor={c1} stopOpacity={0.30} />
                      </linearGradient>
                    );
                  })}
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="name" {...axisProps} />
                <YAxis {...axisProps} />
                <Tooltip content={<TooltipComp />} />
                <Bar dataKey="revenue" name={r.revenue} radius={[8, 8, 2, 2]}>
                  {revenueByCat.map((entry) => (
                    <Cell key={entry.key} fill={`url(#svcRevBar-${entry.key})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassPanel>
      </div>

      {/* ── Monthly Services Trend (stacked area) ───────── */}
      <GlassPanel variant="chartDark" isDark={isDark} className="p-0 overflow-hidden">
        <div className={`px-5 py-3.5 sm:px-6 sm:py-4 border-b ${borderSep} flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0`}>
          <div className="flex items-center gap-2.5">
            <TrendingUp className="w-4 h-4 text-rose-400" style={{ filter: "drop-shadow(0 0 6px rgba(244,63,94,0.5))" }} />
            <h3 className={`text-[13px] font-bold ${txt}`}>{r.monthlyServiceVolume}</h3>
            <span className={`text-[10px] ${txtMuted} hidden sm:inline`}>{r.stackedByCategory}</span>
          </div>
          <ThemedLegend isDark={isDark} items={CATEGORY_KEYS.map(cat => ({ label: categoryLabel(cat), color: CATEGORY_COLORS[cat] || "#64748B" }))} />
        </div>
        <div className="p-4 sm:p-6">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={f.months}>
              <defs>
                {Object.entries(CATEGORY_COLORS).map(([name, color]) => {
                  const grad = CATEGORY_GRADIENTS[name];
                  const c0 = grad ? grad[0] : color;
                  const c1 = grad ? grad[1] : color;
                  return (
                    <linearGradient key={name} id={`svcArea-${name}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={c0} stopOpacity={0.50} />
                      <stop offset="40%" stopColor={c1} stopOpacity={0.15} />
                      <stop offset="100%" stopColor={c1} stopOpacity={0} />
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="month" {...angledAxisProps} />
              <YAxis {...axisProps} />
              <Tooltip content={<TooltipComp />} />
              {CATEGORY_KEYS.map((cat) => (
                <Area
                  key={cat}
                  type="monotone"
                  dataKey={cat}
                  name={categoryLabel(cat)}
                  stackId="1"
                  stroke={CATEGORY_COLORS[cat] || "#64748B"}
                  strokeWidth={1.5}
                  fill={`url(#svcArea-${cat})`}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassPanel>

      {/* ── Service Details Table ───────────────────────── */}
      <GlassPanel variant="chartDark" isDark={isDark} className="overflow-hidden">
        <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-2 sm:pb-3">
          <div className="flex items-center gap-2 mb-1">
            <Scissors className="w-4 h-4 text-rose-400" style={{ filter: "drop-shadow(0 0 6px rgba(251,113,133,0.5))" }} />
            <h3 className={`text-sm font-bold ${txt}`}>{r.allServices}</h3>
          </div>
          <p className={`text-[11px] ${txtMuted}`}>{r.serviceBreakdown}</p>
        </div>
        <div className="px-4 sm:px-6 pb-4 sm:pb-5">
          <div className={`rounded-2xl overflow-x-auto border ${borderSep}`}>
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className={isDark ? "bg-white/[0.03]" : "bg-black/[0.02]"}>
                  <th className={`text-start px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.service}</th>
                  <th className={`text-start px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.category}</th>
                  <th className={`text-end px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.performed}</th>
                  <th className={`text-end px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.revenue}</th>
                  <th className={`text-end px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.averagePriceShort}</th>
                  <th className={`text-end px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.material}</th>
                  <th className={`text-end px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.duration}</th>
                  <th className={`text-end px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.trend}</th>
                </tr>
              </thead>
              <tbody>
                {rankedServices.map((sv, i) => {
                  const margin = Math.round(((sv.avgPrice - sv.avgMaterialCost) / sv.avgPrice) * 100);
                  return (
                    <tr
                      key={sv.id}
                      className={`border-t ${stripeBorder} ${stripeHover} transition-colors ${
                        i % 2 === 0 ? stripeBg : ""
                      }`}
                    >
                      <td className={`px-4 py-3.5 font-semibold ${txt}`}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-1.5 h-6 rounded-full"
                            style={{ backgroundColor: CATEGORY_COLORS[sv.category] || "#64748B" }}
                          />
                          {sv.name}
                        </div>
                      </td>
                      <td className={`px-4 py-3.5 ${txtMid}`}>{categoryLabel(sv.category)}</td>
                      <td className={`text-end px-4 py-3.5 font-bold ${txt}`}>{formatNumber(sv.totalPerformed, lang)}</td>
                      <td className={`text-end px-4 py-3.5 ${txtMid}`}>{fc(sv.revenue)}</td>
                      <td className={`text-end px-4 py-3.5 ${txtMid}`}>{fc(sv.avgPrice)}</td>
                      <td className={`text-end px-4 py-3.5 ${txtMuted}`}>
                        {fc(sv.avgMaterialCost)}
                        <span className={`text-[9px] ${txtFaint} ms-1`}>({margin}%)</span>
                      </td>
                      <td className={`text-end px-4 py-3.5 ${txtMid}`}>{sv.avgDuration} {lang === "he" ? "דק׳" : "min"}</td>
                      <td className="text-end px-4 py-3.5">
                        <span className={`text-[12px] font-semibold ${sv.trend >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          {sv.trend >= 0 ? "+" : ""}{sv.trend}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </GlassPanel>
    </div>
  );
};

export default ServicesReport;
